import { t } from 'i18n';
import React, { ReactNode, useEffect, useState } from 'react';
import { Footer, CustomLink, Messenger } from '@teko/ecommerce';
import { styled, LazyImage, ScrollTopArrow } from '@teko/ui';
import { NavigationBar } from './components/NavigationBar';
import { Area, BannerEntity, JSONObject, ObjectMap } from 'types';
import { useTenantConfig, useTermsAndPolicies } from 'hooks';
import { ForceLogin } from 'components/ForceLogin';
import { MegaMenuItemInterface } from '@teko/types/pb';
import { Topbar, TopbarButton, Zalo } from '@teko/teko-ecommerce';
import { MEMBERSHIP_POLICY_SLUG_PREFIX } from './_constants';
import { getTrackingData } from '@teko/tracking';
import { LazyComponent } from 'components/LazyComponent';
import { StringParam, useQuery } from 'hooks/useQuery';

const TopBannerHeight = styled.div`
  height: 56px;
  @media print {
    display: none;
  }
`;
export interface LayoutProps {
  children: React.ReactNode;
  banners?: BannerEntity[];
  menuItems?: MegaMenuItemInterface[];
  liteLogo?: boolean;
  footerData?: ObjectMap;
  rightMenu?: ReactNode[];
  layoutAreas?: Area[];
}
interface CustomLayoutProps extends LayoutProps {
  leftMenu?: Array<ReactNode>;
  rightMenu?: Array<ReactNode>;
  footer?: React.ReactNode;
  navigationBar?: React.ReactNode;
  forceLogin?: boolean;
}
const DefaultLayout: React.FC<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  forceLogin = false,
}: CustomLayoutProps) => {
  const LoginWrapper = forceLogin ? ForceLogin : React.Fragment;
  const {
    contactLink,
    isDisplayMessenger,
    zaloContactLink,
    partner,
    footerDataConfig,
    noOverride,
  } = useTenantConfig();
  const topBanner = banners?.[0];

  if ((!footer && footerData) || noOverride) {
    const footerLayout = noOverride
      ? {
          ...footerData,
          supportAndPolicy: [
            ...footerDataConfig['supportAndPolicy' as keyof JSONObject],
          ],
        }
      : footerData;
    footer = <Footer footer={footerLayout} />;
  }
  const renderContent = () => {
    // Render custom pages
    // if (allowRenderCustomPage) {
    //   return <>{children}</>;
    // }

    return (
      <>
        {!partner && (
          <>
            {topBanner && (
              <TopBannerHeight
                {...getTrackingData({
                  regionName: 'topBanner',
                  isTrackContent: true,
                })}
              >
                <CustomLink targetPath={topBanner.targetPath}>
                  <LazyImage
                    imageWidth={1920}
                    height={56}
                    cover
                    src={topBanner.image}
                  />
                </CustomLink>
              </TopBannerHeight>
            )}
            {(leftMenu || rightMenu) && (
              <Topbar leftMenu={leftMenu} rightMenu={rightMenu} />
            )}
          </>
        )}
        {navigationBar}
        {<div>{children}</div>}
        <LazyComponent>{footer}</LazyComponent>
      </>
    );
  };

  return (
    <>
      <LoginWrapper>{renderContent()}</LoginWrapper>
      <ScrollTopArrow />
      {contactLink && isDisplayMessenger && <Messenger url={contactLink} />}
      {zaloContactLink && <Zalo url={zaloContactLink} />}
    </>
  );
};
export const Layout: React.FunctionComponent<CustomLayoutProps> = ({
  children,
  banners,
  leftMenu,
  rightMenu,
  footerData,
  footer,
  navigationBar,
  menuItems,
  liteLogo,
  forceLogin = false,
  layoutAreas,
}: CustomLayoutProps) => {
  const { identityRequired, hasNews } = useTenantConfig();
  const { termsAndPolicies } = useTermsAndPolicies();
  const [requireLogin, setRequireLogin] = useState(forceLogin);
  const finalRightActions: Array<ReactNode> = hasNews
    ? [
        <TopbarButton key={0} href="/tin-tuc">
          {t('Tin tức')}
        </TopbarButton>,
        ...rightMenu,
      ]
    : rightMenu;
  const query = useQuery({ ref_code: StringParam });

  useEffect(() => {
    if (identityRequired && query?.ref_code) {
      sessionStorage.setItem('ref_code', query.ref_code.toString());
      setRequireLogin(true);
    } else if (!!sessionStorage.getItem('ref_code')) {
      // for case redirected back after login
      setRequireLogin(true);
    }
  }, []);

  if (!footer && footerData) {
    if (
      termsAndPolicies.length > 0 &&
      !!footerData.supportAndPolicy?.[0]?.links
    ) {
      footerData.supportAndPolicy[0].links = termsAndPolicies
        .filter(({ slug }) => !slug.includes(MEMBERSHIP_POLICY_SLUG_PREFIX))
        .map(({ title, slug }) => ({
          children: t(title),
          href: `/${slug}`,
          target: '_blank',
        }));
    }
    footer = <Footer footer={footerData} />;
  }

  if (!navigationBar) {
    navigationBar = (
      <NavigationBar
        menuItems={menuItems}
        showShippingFeeMiniCart={false}
        liteLogo={liteLogo}
      />
    );
  }
  return (
    <DefaultLayout
      leftMenu={leftMenu}
      rightMenu={finalRightActions}
      navigationBar={navigationBar}
      footer={footer}
      footerData={footerData}
      banners={banners}
      forceLogin={requireLogin}
      layoutAreas={layoutAreas}
    >
      {children}
    </DefaultLayout>
  );
};
